import { useMemo, useState } from 'react';
import { CommonUtility } from '@utility/common';
import { useDebouncedEffect } from '@hooks/debounceAsync';
import { ToastMessage } from '@utility/toast';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import { AppModal } from '@elements/Modal';
import { FIELD_INPUT_INVALID } from '@elements/form-field/styles';
import { DeleteIcon } from '../styles/common';
import { DangerText, StyledCheckbox } from '@elements/Common';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import { PalletQuickView } from './quickView';
import { AppTable, AppTableRow, AppTableCell } from '@components/AppTable';
import type { ShipmentDynamicFieldType } from '@hooks/shipment';
import type { ITableColumn } from 'types/common/table';

const maskOptions = {
  allowDecimal: true,
  decimalLimit: 1,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
};

type PalletRowProps = {
  item?: SavePalletType;
  onRowChange: (index: number, key: string, val: any) => void;
  index?: number;
  removeBox: (index: number) => void;
  totalItems: number;
  errors: errorType;
};

function PalletRow({
  item,
  onRowChange,
  index,
  removeBox,
  totalItems,
  errors,
}: PalletRowProps) {
  const onChange = (key: string, value: any) => {
    onRowChange(index || 0, key, value);
  };

  const key = `pallet-${index}`;

  if (!item) {
    return null;
  }

  return (
    <AppTableRow verticalAlign="top">
      <AppTableCell>
        <div className="sk:flex! sk:items-start!">
          <div className="sk:w-17.5">
            <NumberMaskInput
              name="length"
              placeholder="L"
              value={item.length}
              maxValue={item.maskOptions?.maxValue}
              maskOptions={maskOptions}
              onChange={value => onChange('length', value)}
              disabled
            />
          </div>
          <span className="sk:mx-2! sk:mt-3.5!">
            <XIcon />
          </span>
          <div className="sk:w-17.5">
            <NumberMaskInput
              name="width"
              placeholder="W"
              value={item.width}
              maxValue={item.maskOptions?.maxValue}
              maskOptions={maskOptions}
              onChange={value => onChange('width', value)}
              disabled
            />
          </div>
          <span className="sk:mx-2! sk:mt-3.5!">
            <XIcon />
          </span>
          <div className="sk:flex! sk:flex-col!">
            <div className="sk:w-17.5">
              <NumberMaskInput
                name="height"
                placeholder="H"
                value={item.height}
                maxValue={item.maskOptions?.maxValue}
                maskOptions={maskOptions}
                onChange={value => onChange('height', value)}
                className={
                  errors[key]?.height ? FIELD_INPUT_INVALID : undefined
                }
              />
            </div>
            {!!errors[key]?.height && (
              <DangerText>{errors[key]?.height}</DangerText>
            )}
          </div>
        </div>
      </AppTableCell>
      <AppTableCell>
        <div className="sk:w-20">
          <NumberMaskInput
            name="weight"
            placeholder="Weight"
            value={item.weight}
            maxValue={item.maskOptions?.maxValue}
            maskOptions={maskOptions}
            onChange={value => onChange('weight', value)}
            className={errors[key]?.weight ? FIELD_INPUT_INVALID : undefined}
          />
        </div>
        {!!errors[key]?.weight && (
          <DangerText>{errors[key]?.weight}</DangerText>
        )}
      </AppTableCell>
      <AppTableCell>
        <div className="sk:mt-2!">
          <StyledCheckbox
            checked={item.isStacked}
            onChange={(e, { checked }) => onChange('isStacked', checked)}
          />
        </div>
      </AppTableCell>
      <AppTableCell>
        {totalItems > 1 && (
          <div className="sk:mt-2!">
            <DeleteIcon onClick={() => removeBox(index || 0)} size={20} />
          </div>
        )}
      </AppTableCell>
    </AppTableRow>
  );
}

type PalletType = {
  dimensions: {
    length: number;
    width: number;
    height: number;
    unitOfMeasurement: string;
  };
  weight: {
    value: number;
    unit: string;
  };
  quantity: number;
  isStackable: boolean;
};

type SavePalletType = {
  length: number;
  width: number;
  height: number;
  weight: number;
  dimensionUnit: string;
  weightUnit: string;
  isStacked: boolean;
  maskOptions?: Record<string, any>;
};

type PalletBoxPopupProps = {
  value: PalletType[];
  item: ShipmentDynamicFieldType;
  onChange: (name: string, value: PalletType[]) => void;
};

type errorType = {
  [key: string]: {
    height?: string;
    weight?: string;
  };
};

export function PalletBoxPopup({ value, item, onChange }: PalletBoxPopupProps) {
  const [open, setOpen] = useState(false);
  const [formValue, setFormValue] = useState<SavePalletType[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setError] = useState<errorType>({});

  const closePopup = () => {
    setOpen(false);
  };

  const dummyItem: SavePalletType = {
    length: 48,
    width: 40,
    height: 0,
    weight: 0,
    dimensionUnit: 'in',
    weightUnit: 'lb',
    isStacked: false,
  };

  useDebouncedEffect(
    () => {
      if (CommonUtility.isValidArray(value)) {
        const newValue: SavePalletType[] = value.map(x => ({
          length: x.dimensions.length,
          width: x.dimensions.width,
          height: x.dimensions.height,
          weight: x.weight.value,
          dimensionUnit: x.dimensions.unitOfMeasurement,
          weightUnit: x.weight.unit,
          isStacked: x.isStackable,
        }));

        setFormValue(JSON.parse(JSON.stringify(newValue)));
      } else {
        setFormValue([]);
      }
      setSubmitted(false);
      setError({});
    },
    [value, open],
    500,
  );

  const errorObject = (): errorType => {
    const error: errorType = {};
    formValue.forEach((pallet, index) => {
      const key = `pallet-${index}`;
      error[key] = {};

      if (!pallet.height) {
        error[key].height = 'Height is a required field';
      } else if (pallet.height > 200) {
        error[key].height = 'Height must be less than 201';
      }

      if (!pallet.weight) {
        error[key].weight = 'Weight is a required field';
      } else if (pallet.weight > 20000) {
        error[key].weight = 'Weight must be less than 20001';
      }

      if (!CommonUtility.isValidObject(error[key])) {
        delete error[key];
      }
    });
    return error;
  };

  const onRowChange = (index: number, key: string, keyValue: any) => {
    const newValue = [...formValue];
    newValue[index][key] = keyValue;
    if (submitted) {
      const error = errorObject();
      setError(error);
    }
    setFormValue(newValue);
  };

  const addBox = () => {
    const arr = [...formValue];
    arr.push(dummyItem);
    setFormValue(arr);
  };

  const removeBox = (index: number) => {
    let arr = [...formValue];
    arr = arr.filter((x, i) => i !== index);
    setFormValue(arr);
  };

  const columns: ITableColumn[] = [
    {
      text: 'Dimensions (in.)',
      key: 'dimension',
      textAlign: 'left',
      width: 5,
    },
    {
      text: 'Weight (lb)',
      key: 'weight',
      textAlign: 'left',
      width: 2,
    },
    {
      text: 'Stacked Pallets',
      key: 'stacked pallets',
      textAlign: 'left',
      width: 2,
    },
    {
      text: '',
      key: 'actions',
      width: 1,
    },
  ];

  const totalItems = useMemo(() => {
    if (CommonUtility.isValidArray(value)) {
      return value.length;
    }
    return 0;
  }, [value]);

  const onConfirm = () => {
    const error = errorObject();
    if (CommonUtility.isValidObject(error)) {
      ToastMessage.error('Kindly resolve errors');
      setSubmitted(true);
      setError(error);
    } else {
      const parsedValue: PalletType[] = formValue.map(pallet => ({
        dimensions: {
          length: Number(pallet.length),
          width: Number(pallet.width),
          height: Number(pallet.height),
          unitOfMeasurement: pallet.dimensionUnit,
        },
        weight: {
          value: Number(pallet.weight),
          unit: pallet.weightUnit,
        },
        quantity: 1,
        isStackable: pallet.isStacked,
      }));
      onChange(item.name, parsedValue);
      closePopup();
    }
  };

  return (
    <>
      {totalItems > 0 ? (
        <PalletQuickView
          totalItems={totalItems}
          value={value}
          openPopup={() => setOpen(true)}
        />
      ) : (
        <HoverBorderButton
          onClick={() => setOpen(true)}
          className="flex-btn sk:p-1!"
        >
          <PlusIcon />
        </HoverBorderButton>
      )}
      <AppModal
        size="small"
        open={open}
        closeModal={closePopup}
        title="Add PalletType Information"
        footer={<StyledButton onClick={onConfirm}>Confirm</StyledButton>}
      >
        <div className="sk:mb-4">
          <b>PalletType Information</b>
          <AppTable
            data={CommonUtility.isValidArray(formValue) ? formValue : []}
            columns={columns}
            RowComponent={
              <PalletRow
                totalItems={totalItems}
                removeBox={removeBox}
                onRowChange={onRowChange}
                errors={errors}
              />
            }
            noShadow
          />
          <HoverBorderButton
            onClick={addBox}
            className="flex-btn sk:mt-2!"
            type="button"
          >
            <PlusIcon className="sk:mr-1!" /> Pallet
          </HoverBorderButton>
        </div>
      </AppModal>
    </>
  );
}
